import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Surface, Button } from "@/components/ui-kit";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LineChart, Line, Legend } from "recharts";
import { DownloadSimple } from "@phosphor-icons/react";
import { downloadCsv, regSort } from "@/lib/csv";

export default function Reports() {
    const [tab, setTab] = useState("cost");
    const [costData, setCostData] = useState([]);
    const [mtbfData, setMtbfData] = useState([]);
    const [turnover, setTurnover] = useState([]);
    const [tyrePerf, setTyrePerf] = useState([]);
    const [trendData, setTrendData] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [trendVehicleId, setTrendVehicleId] = useState("");
    const [disposals, setDisposals] = useState({ total_amount_inr: 0, total_count: 0, breakdown: {}, rows: [] });
    const [dispFrom, setDispFrom] = useState("");
    const [dispTo, setDispTo] = useState("");

    useEffect(() => {
        api.get("/reports/cost-per-vehicle").then((r) => setCostData(r.data)).catch(() => {});
        api.get("/reports/mtbf-mttr").then((r) => setMtbfData(r.data)).catch(() => {});
        api.get("/reports/inventory-turnover").then((r) => setTurnover(r.data)).catch(() => {});
        api.get("/reports/tyre-performance").then((r) => setTyrePerf(r.data)).catch(() => {});
        api.get("/vehicles?include_sold=true").then((r) => setVehicles(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const p = new URLSearchParams();
        if (dispFrom) p.set("from_date", dispFrom);
        if (dispTo) p.set("to_date", dispTo);
        api.get(`/reports/disposals${p.toString() ? `?${p.toString()}` : ""}`)
            .then((r) => setDisposals(r.data)).catch(() => {});
    }, [dispFrom, dispTo]);

    useEffect(() => {
        const url = trendVehicleId
            ? `/reports/maintenance-trend?vehicle_id=${trendVehicleId}`
            : "/reports/maintenance-trend";
        api.get(url).then((r) => setTrendData(r.data)).catch(() => {});
    }, [trendVehicleId]);

    const tabs = [
        ["cost", "Cost / Vehicle"],
        ["trend", "Maintenance Trend"],
        ["mtbf", "MTBF / MTTR"],
        ["turnover", "Inventory Turnover"],
        ["tyre", "Tyre Performance"],
        ["disposals", "Disposals"],
    ];

    const exportCostCsv = () => downloadCsv("cost-per-vehicle.csv", costData, [
        ["REG", "reg_number"], ["MAKE", "make"], ["MODEL", "model"],
        ["WO COUNT", "wo_count"], ["TOTAL COST (INR)", "total_cost"],
        ["ODOMETER (KM)", "odometer"], ["COST PER KM (INR)", "cost_per_km"],
    ]);
    const exportTrendCsv = () => downloadCsv("maintenance-trend.csv", trendData, [
        ["MONTH", "month"], ["PREVENTIVE", "preventive"],
        ["BREAKDOWN", "breakdown"], ["INSPECTION", "inspection"], ["TOTAL", "total"],
    ]);
    const exportMtbfCsv = () => downloadCsv("mtbf-mttr.csv", mtbfData, [
        ["REG", "reg_number"], ["BREAKDOWNS", "breakdowns"], ["MTBF KM", "mtbf_km"],
    ]);
    const exportTurnoverCsv = () => downloadCsv("inventory-turnover.csv", turnover, [
        ["SKU", "sku"], ["NAME", "name"], ["ISSUED QTY", "issued_qty"],
        ["ISSUED COST (INR)", "issued_cost"], ["CURRENT STOCK", "current_stock"],
    ]);
    const exportTyreCsv = () => downloadCsv("tyre-performance.csv", tyrePerf, [
        ["SERIAL", "serial_number"], ["BRAND", "brand"], ["SIZE", "size"],
        ["TOTAL KM", "total_km"], ["RETREADS", "retread_count"],
        ["COST PER KM (INR)", "cost_per_km"], ["STATUS", "status"],
    ]);

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader eyebrow="ANALYTICS" title="Reports" subtitle="Operational metrics across fleet, maintenance, inventory and tyres." />

            <div className="flex gap-1 border-b border-fleet-border mb-6">
                {tabs.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`report-tab-${k}`}
                        className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-fleet-ink text-fleet-ink font-medium" : "border-transparent text-fleet-inkSoft hover:text-fleet-ink"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === "cost" && (
                <Surface className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-mono">COST PER KM (₹) BY VEHICLE</div>
                        <Button variant="secondary" size="sm" onClick={exportCostCsv} data-testid="report-cost-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={costData}>
                                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="reg_number" stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Bar dataKey="cost_per_km" fill="#0F172A" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <table className="w-full text-sm mt-6">
                        <thead><tr className="text-left">
                            {["REG", "MAKE/MODEL", "WO COUNT", "TOTAL COST", "ODO", "₹/KM"].map((h) => <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {costData.map((c) => (
                                <tr key={c.vehicle_id} className="border-t border-fleet-border">
                                    <td className="px-3 py-2 font-mono">{c.reg_number}</td>
                                    <td className="px-3 py-2">{c.make} {c.model}</td>
                                    <td className="px-3 py-2 font-mono">{c.wo_count}</td>
                                    <td className="px-3 py-2 font-mono">₹{(c.total_cost || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-3 py-2 font-mono">{c.odometer?.toLocaleString()}</td>
                                    <td className="px-3 py-2 font-mono">₹{c.cost_per_km}</td>
                                </tr>
                            ))}
                            {costData.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-fleet-inkSoft">No data yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "trend" && (
                <Surface className="p-6">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <div>
                            <div className="label-mono">MAINTENANCE COST TREND · 6 months</div>
                            <p className="text-xs text-fleet-inkSoft mt-0.5">Monthly spend split by work-order type — useful to spot rising breakdown costs.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="label-mono">VEHICLE</span>
                            <select
                                value={trendVehicleId}
                                onChange={(e) => setTrendVehicleId(e.target.value)}
                                className="px-3 py-1.5 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink min-w-[180px]"
                                data-testid="trend-vehicle-select"
                            >
                                <option value="">All vehicles</option>
                                {[...vehicles].sort(regSort).map((v) => (
                                    <option key={v.id} value={v.id}>{v.reg_number} · {v.make} {v.model}</option>
                                ))}
                            </select>
                            <Button variant="secondary" size="sm" onClick={exportTrendCsv} data-testid="report-trend-csv">
                                <DownloadSimple size={14} /> CSV
                            </Button>
                        </div>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="month" stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }}
                                    formatter={(v) => `₹${(v || 0).toLocaleString("en-IN")}`} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="preventive" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} name="Preventive" />
                                <Line type="monotone" dataKey="breakdown" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} name="Breakdown" />
                                <Line type="monotone" dataKey="inspection" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} name="Inspection" />
                                <Line type="monotone" dataKey="total" stroke="#10B981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} name="Total" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <table className="w-full text-sm mt-6">
                        <thead><tr className="text-left">
                            {["MONTH", "PREVENTIVE", "BREAKDOWN", "INSPECTION", "TOTAL"].map((h) => <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {trendData.map((m) => (
                                <tr key={m.month} className="border-t border-fleet-border" data-testid={`trend-row-${m.month}`}>
                                    <td className="px-3 py-2 font-mono">{m.month}</td>
                                    <td className="px-3 py-2 font-mono">₹{(m.preventive || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-3 py-2 font-mono">₹{(m.breakdown || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-3 py-2 font-mono">₹{(m.inspection || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-3 py-2 font-mono font-semibold">₹{(m.total || 0).toLocaleString("en-IN")}</td>
                                </tr>
                            ))}
                            {trendData.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-fleet-inkSoft">No work orders logged yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "mtbf" && (
                <Surface className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-mono">MEAN TIME BETWEEN FAILURES (km)</div>
                        <Button variant="secondary" size="sm" onClick={exportMtbfCsv} data-testid="report-mtbf-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                    </div>
                    <table className="w-full text-sm">
                        <thead><tr className="text-left">
                            {["REG", "BREAKDOWNS", "MTBF (KM)"].map((h) => <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {mtbfData.map((m) => (
                                <tr key={m.vehicle_id} className="border-t border-fleet-border">
                                    <td className="px-3 py-2 font-mono">{m.reg_number}</td>
                                    <td className="px-3 py-2 font-mono">{m.breakdowns}</td>
                                    <td className="px-3 py-2 font-mono">{m.mtbf_km?.toLocaleString()}</td>
                                </tr>
                            ))}
                            {mtbfData.length === 0 && <tr><td colSpan={3} className="px-3 py-10 text-center text-sm text-fleet-inkSoft">No breakdowns logged yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "turnover" && (
                <Surface className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-mono">INVENTORY TURNOVER</div>
                        <Button variant="secondary" size="sm" onClick={exportTurnoverCsv} data-testid="report-turnover-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={turnover}>
                                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="sku" stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Bar dataKey="issued_qty" fill="#2563EB" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <table className="w-full text-sm mt-6">
                        <thead><tr className="text-left">
                            {["SKU", "NAME", "ISSUED QTY", "ISSUED COST", "STOCK"].map((h) => <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {turnover.map((t) => (
                                <tr key={t.inventory_id} className="border-t border-fleet-border">
                                    <td className="px-3 py-2 font-mono">{t.sku}</td>
                                    <td className="px-3 py-2">{t.name}</td>
                                    <td className="px-3 py-2 font-mono">{t.issued_qty}</td>
                                    <td className="px-3 py-2 font-mono">₹{(t.issued_cost || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-3 py-2 font-mono">{t.current_stock}</td>
                                </tr>
                            ))}
                            {turnover.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-fleet-inkSoft">No issues recorded yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "tyre" && (
                <Surface className="p-6">
                    <div className="label-mono mb-4">TYRE PERFORMANCE</div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={tyrePerf}>
                                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="serial_number" stroke="#94A3B8" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                <YAxis stroke="#94A3B8" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Bar dataKey="total_km" fill="#10B981" radius={[2, 2, 0, 0]}>
                                    {tyrePerf.map((entry, i) => (
                                        <Cell key={entry.tyre_id || entry.serial_number || i} fill={entry.status === "scrapped" ? "#94A3B8" : "#10B981"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <table className="w-full text-sm mt-6">
                        <thead><tr className="text-left">
                            {["SERIAL", "BRAND", "SIZE", "TOTAL KM", "RETREADS", "₹/KM", "STATUS"].map((h) => <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {tyrePerf.map((t) => (
                                <tr key={t.id} className="border-t border-fleet-border">
                                    <td className="px-3 py-2 font-mono">{t.serial_number}</td>
                                    <td className="px-3 py-2">{t.brand}</td>
                                    <td className="px-3 py-2 font-mono">{t.size}</td>
                                    <td className="px-3 py-2 font-mono">{(t.total_km || 0).toLocaleString()}</td>
                                    <td className="px-3 py-2 font-mono">{t.retread_count}</td>
                                    <td className="px-3 py-2 font-mono">₹{t.cost_per_km}</td>
                                    <td className="px-3 py-2 capitalize">{t.status}</td>
                                </tr>
                            ))}
                            {tyrePerf.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-fleet-inkSoft">No tyre data yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "disposals" && (
                <Surface className="p-5">
                    <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="disposals-filters">
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">From date</label>
                            <input type="date" value={dispFrom} onChange={(e) => setDispFrom(e.target.value)} className="border border-fleet-border rounded-md px-3 py-2 text-sm" data-testid="disposals-from" />
                        </div>
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">To date</label>
                            <input type="date" value={dispTo} onChange={(e) => setDispTo(e.target.value)} className="border border-fleet-border rounded-md px-3 py-2 text-sm" data-testid="disposals-to" />
                        </div>
                        {(dispFrom || dispTo) && (
                            <button onClick={() => { setDispFrom(""); setDispTo(""); }} className="self-end text-xs text-fleet-inkSoft hover:text-fleet-ink underline">Clear</button>
                        )}
                        <Button variant="secondary" size="sm" className="ml-auto self-end"
                            onClick={() => downloadCsv(`disposals-${new Date().toISOString().slice(0,10)}.csv`, disposals.rows, [
                                ["TYPE", "entity_type"], ["ID", "id"], ["LABEL", "label"],
                                ["SALE DATE", "sale_date"], ["BUYER", "buyer_name"], ["PHONE", "buyer_phone"],
                                ["AMOUNT (INR)", "sale_amount_inr"], ["NOTES", "notes"],
                            ])}
                            data-testid="disposals-export-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
                        <div className="border border-fleet-border rounded-md p-4 bg-slate-50" data-testid="disposals-total">
                            <div className="label-mono text-xs text-fleet-inkSoft">TOTAL REVENUE</div>
                            <div className="text-2xl font-mono mt-1">₹{(disposals.total_amount_inr || 0).toLocaleString("en-IN")}</div>
                            <div className="text-xs text-fleet-inkSoft mt-1">{disposals.total_count} asset(s)</div>
                        </div>
                        {[
                            { key: "vehicle", label: "VEHICLES" },
                            { key: "tyre", label: "TYRES" },
                            { key: "battery", label: "BATTERIES" },
                        ].map(({ key, label }) => (
                            <div key={key} className="border border-fleet-border rounded-md p-4" data-testid={`disposals-${key}`}>
                                <div className="label-mono text-xs text-fleet-inkSoft">{label}</div>
                                <div className="text-2xl font-mono mt-1">₹{((disposals.breakdown?.[key]?.amount_inr) || 0).toLocaleString("en-IN")}</div>
                                <div className="text-xs text-fleet-inkSoft mt-1">{disposals.breakdown?.[key]?.count || 0} sold</div>
                            </div>
                        ))}
                    </div>

                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left">
                                {["TYPE", "LABEL", "SALE DATE", "BUYER", "PHONE", "AMOUNT (₹)"].map((h) => (
                                    <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {disposals.rows?.map((r) => (
                                <tr key={`${r.entity_type}-${r.id}`} className="border-t border-fleet-border" data-testid={`disposal-row-${r.id}`}>
                                    <td className="px-3 py-2 uppercase text-xs">{r.entity_type}</td>
                                    <td className="px-3 py-2 font-medium">{r.label || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.sale_date || "—"}</td>
                                    <td className="px-3 py-2">{r.buyer_name || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.buyer_phone || "—"}</td>
                                    <td className="px-3 py-2 font-mono">₹{(r.sale_amount_inr || 0).toLocaleString("en-IN")}</td>
                                </tr>
                            ))}
                            {(!disposals.rows || disposals.rows.length === 0) && (
                                <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-fleet-inkSoft">No disposals in this window.</td></tr>
                            )}
                        </tbody>
                    </table>
                </Surface>
            )}
        </div>
    );
}
